import { getENVPath } from 'helper';

export interface ICategoryFaqItem {
  question: string;
  answer: string;
  answerList?: string[];
  answerLink?: { label: string; href: string };
  answerLinks?: { label: string; href: string }[];
  answerSuffix?: string;
}

interface ICategoryFaqInput {
  categoryTitle?: string;
  categorySlug?: string;
  hiringCount?: number;
  topCompaniesHiring?: { name: string; slug: string }[];
  jobResults?: { careerLevel?: string[] | null }[];
}

const formatCategoryName = (categoryTitle = '', categorySlug = '') => {
  if (categoryTitle.trim()) {
    let normalizedTitle = categoryTitle.trim();
    const lowerTitle = normalizedTitle.toLowerCase();

    if (lowerTitle.endsWith(' - work from home')) {
      normalizedTitle = normalizedTitle.slice(0, -17).trim();
    }

    while (normalizedTitle.toLowerCase().startsWith('remote ')) {
      normalizedTitle = normalizedTitle.slice(7).trim();
    }

    while (normalizedTitle.toLowerCase().endsWith(' jobs')) {
      normalizedTitle = normalizedTitle.slice(0, -5).trim();
    }
    return normalizedTitle;
  }

  const normalizedSlug = categorySlug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return normalizedSlug ? `Remote ${normalizedSlug} Jobs` : '';
};

const formatTopCompanies = (
  companies: { name: string; slug: string }[] = []
): { label: string; href: string }[] => {
  return companies.reduce<{ label: string; href: string }[]>(
    (formattedCompanies, company) => {
      const label = company.name.trim();
      if (label && company.slug) {
        formattedCompanies.push({
          label,
          href: `${getENVPath().NEXT_PUBLIC_ROOT_URL}${company.slug}`,
        });
      }
      return formattedCompanies;
    },
    []
  );
};

const joinSchemaList = (values: Array<string | undefined | null>) => {
  const cleanValues = values.filter(
    (value): value is string =>
      typeof value === 'string' && value.trim().length > 0
  );

  if (cleanValues.length === 0) {
    return '';
  }

  if (cleanValues.length === 1) {
    return cleanValues[0];
  }

  if (cleanValues.length === 2) {
    return `${cleanValues[0]} and ${cleanValues[1]}`;
  }

  return `${cleanValues.slice(0, -1).join(', ')}, and ${
    cleanValues[cleanValues.length - 1]
  }`;
};

const formatCareerLevelBreakdown = (
  jobResults: { careerLevel?: string[] | null }[] = []
): string[] | null => {
  const counts = jobResults.reduce(
    (breakdown, job) => {
      const levels = (job.careerLevel || []).map((level) =>
        level.toLowerCase()
      );

      if (levels.some((level) => level.includes('entry'))) {
        breakdown.entryLevel += 1;
      }
      if (levels.some((level) => level.includes('experienced'))) {
        breakdown.experienced += 1;
      }
      if (
        levels.some(
          (level) => level.includes('manager') && !level.includes('senior')
        )
      ) {
        breakdown.manager += 1;
      }
      if (
        levels.some(
          (level) => level.includes('senior') && level.includes('manager')
        )
      ) {
        breakdown.seniorManager += 1;
      }

      return breakdown;
    },
    { entryLevel: 0, experienced: 0, manager: 0, seniorManager: 0 }
  );

  if (
    counts.entryLevel === 0 &&
    counts.experienced === 0 &&
    counts.manager === 0 &&
    counts.seniorManager === 0
  ) {
    return null;
  }

  return [
    `Entry-level: ${counts.entryLevel}`,
    `Experienced: ${counts.experienced}`,
    `Manager and senior-level manager: ${
      counts.manager + counts.seniorManager
    }`,
  ];
};

export const generateCategoryFaqItems = ({
  categoryTitle,
  categorySlug,
  hiringCount,
  topCompaniesHiring,
  jobResults,
}: ICategoryFaqInput): ICategoryFaqItem[] => {
  const categoryName = formatCategoryName(
    categoryTitle,
    categorySlug
  ).toLocaleLowerCase();
  if (!categoryName) {
    return [];
  }

  const jobsCountText =
    typeof hiringCount === 'number' && hiringCount > 0
      ? `There are ${hiringCount.toLocaleString(
          'en-US'
        )} fully remote jobs available today.`
      : 'There are no fully remote roles currently available in this category.';
  const careerLevelBreakdown = formatCareerLevelBreakdown(jobResults);
  const topCompanies = formatTopCompanies(topCompaniesHiring);
  const categoryFullName = `Remote ${categoryName} Jobs`.toLocaleLowerCase();
  return [
    {
      question: `How many ${categoryFullName} are available on Remote.co?`,
      answer: jobsCountText,
    },
    {
      question: `What education level do I need for a ${categoryFullName}?`,
      answer: `Most ${categoryFullName} require at least a bachelor's degree, though requirements vary by employer.`,
    },
    ...(careerLevelBreakdown
      ? [
          {
            question: `How many ${categoryFullName} are available on Remote.co by career level?`,
            answer: `The breakdown of ${categoryFullName} by career level is as follows:`,
            answerList: careerLevelBreakdown,
          },
        ]
      : []),
    {
      question: `How can I apply for ${categoryFullName} on Remote.co?`,
      answer: `You can directly apply to ${categoryFullName} by clicking the “Apply” button.`,
    },
    {
      question: `Are these ${categoryFullName} full-time or part-time?`,
      answer: `${
        categoryFullName.charAt(0).toUpperCase() + categoryFullName.slice(1)
      } opportunities on `,
      answerLink: {
        label: 'Remote.co',
        href: getENVPath().NEXT_PUBLIC_ROOT_URL || '/',
      },
      answerSuffix:
        ' are available in alternative schedule, flexible schedule, full-time, and part-time.',
    },
    {
      question: `Are ${categoryFullName} available worldwide?`,
      answer:
        'Some employers hire globally, while others restrict hiring to specific countries, states, or time zones due to tax, regulatory, payroll, or collaboration requirements. Always review location requirements in the job description.',
    },
    ...(topCompanies.length
      ? [
          {
            question: `Which top companies are hiring for ${categoryFullName}?`,
            answer: `These are some of the top companies hiring for ${categoryFullName}:`,
            answerLinks: topCompanies,
          },
        ]
      : []),
    {
      question: `Are ${categoryFullName} listed on Remote.co legit?`,
      answer: 'Unlike many job boards that rely solely on automated listings, ',
      answerLink: {
        label: 'Remote.co',
        href: getENVPath().NEXT_PUBLIC_ROOT_URL || '/',
      },
      answerSuffix: ` features ${categoryFullName} that are reviewed and curated by our team. This hands-on approach helps you spend less time filtering through opportunities and more time applying for roles that match your skills, whether you are looking for full-time, part-time, freelance, or temporary work.`,
    },
    {
      question: `How do I prepare for a ${categoryFullName} interview?`,
      answer: `Review ${categoryName} core principles of the field, software proficiency, and reporting processes, and be prepared to discuss how you manage deadlines and communication while working remotely.`,
    },
  ];
};

export const createCategoryFaqSchema = (faqItems: ICategoryFaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: [
        item.answer,
        joinSchemaList([
          item.answerLink?.label,
          ...(item.answerLinks?.map((link) => link.label) || []),
          ...(item.answerList || []),
        ]),
        item.answerSuffix,
      ]
        .filter(Boolean)
        .join(' '),
    },
  })),
});

export const serializeJsonLd = (jsonLd: unknown) =>
  JSON.stringify(jsonLd).replace(/</g, '\\u003c');
